import React, { useState } from 'react';
import { FormikProps, Formik } from 'formik';
import { Form, Input } from 'formik-antd';
import * as Yup from 'yup';
import { Row, Col } from 'antd';
import { RadioChangeEvent } from 'antd/lib/radio';

import Radio from 'components/formik/Radio';
import FormikEffect from 'components/formik/FormikEffect';
import FormItem from 'components/formik/FormItem';
import { CustomerPreScreeningProviderEnum } from 'api/adminApiClient';
import { getCustomerPreScreeningBilledLabel } from 'utils/clientUtils';
import { ResourceDownloadResponse } from 'api/backofficeApiClient';
import OnboardingDocumentationUploader from './OnboardingDocumentationUploader';

type Props = {
  formRef: any;
  onChange: (formik: FormikProps<AssignmentRequirementsFormValues>) => void;
  onboardingDocumentationFileInfo?: ResourceDownloadResponse[];
  initialValues?: AssignmentRequirementsFormValues;
};

export interface AssignmentRequirementsFormValues {
  contractorsPermitted?: boolean;
  preScreeningRequired?: boolean;
  preScreeningBilledTo?: CustomerPreScreeningProviderEnum;
  preScreening: string;
  designatedPreScreening?: boolean;
  isOnboardingDocumentationRequired?: boolean;
  specialInstructions: string;
  onboardingDocumentation: string[];
  terminationInstructions: string;
  workPerformance: string;
  tempToPermConversion: string;
  timeEntry: string;
  assignmentNotes: string;
}

const maxCharactersLength = 250;

const validationSchema = Yup.object({
  contractorsPermitted: Yup.boolean(),
  preScreeningRequired: Yup.boolean(),
  preScreeningBilledTo: Yup.number(),
  preScreening: Yup.string()
    .trim()
    .max(120, `Pre-screening should be 120 characters maximum`),
  designatedPreScreening: Yup.boolean(),
  isOnboardingDocumentationRequired: Yup.boolean(),
  specialInstructions: Yup.string()
    .trim()
    .max(
      maxCharactersLength,
      `Special instructions should be ${maxCharactersLength} characters maximum`,
    ),
  onboardingDocumentation: Yup.array().of(Yup.string()),
  terminationInstructions: Yup.string()
    .trim()
    .max(
      maxCharactersLength,
      `Termination instructions should be ${maxCharactersLength} characters maximum`,
    ),
  workPerformance: Yup.string()
    .trim()
    .max(
      maxCharactersLength,
      `Guaranteed work performance should be ${maxCharactersLength} characters maximum`,
    ),
  tempToPermConversion: Yup.string()
    .trim()
    .max(
      maxCharactersLength,
      `Temp-to-perm conversion should be ${maxCharactersLength} characters maximum`,
    ),
  timeEntry: Yup.string()
    .trim()
    .max(120, `Time entry method should be 120 characters maximum`),
  assignmentNotes: Yup.string()
    .trim()
    .max(
      maxCharactersLength,
      `Added notes should be ${maxCharactersLength} characters maximum`,
    ),
});

const preScreeningBilledMemberLabel = getCustomerPreScreeningBilledLabel(
  CustomerPreScreeningProviderEnum.Member,
);
const preScreeningBilledMBPLabel = getCustomerPreScreeningBilledLabel(
  CustomerPreScreeningProviderEnum.MBP,
);

const preScreeningBilledCustomerLabel = getCustomerPreScreeningBilledLabel(
  CustomerPreScreeningProviderEnum.Customer,
);

export default function AssignmentRequirements(props: Props) {
  const {
    formRef,
    initialValues: predefinedInitialValues,
    onChange,
    onboardingDocumentationFileInfo: defaultOnboardingDocumentationFileInfo,
  } = props;
  const initialValues: AssignmentRequirementsFormValues = {
    contractorsPermitted: undefined,
    preScreeningRequired: undefined,
    preScreeningBilledTo: undefined,
    preScreening: '',
    designatedPreScreening: undefined,
    isOnboardingDocumentationRequired: undefined,
    specialInstructions: '',
    onboardingDocumentation: [],
    terminationInstructions: '',
    workPerformance: '',
    tempToPermConversion: '',
    timeEntry: '',
    assignmentNotes: '',
  };
  const [onboardingDocumentationFileInfo, setOnboardingDocumentationFileInfo] =
    useState(defaultOnboardingDocumentationFileInfo);
  const handlePreScreeningChange = (
    ev: RadioChangeEvent,
    formik: FormikProps<AssignmentRequirementsFormValues>,
  ) => {
    if (ev.target.value) {
      formik.setFieldValue(
        'preScreeningBilledTo',
        CustomerPreScreeningProviderEnum.Member,
        false,
      );

      return;
    }

    formik.setFieldValue('preScreeningBilledTo', undefined, false);
  };

  const handleOnboardingDocumentationChange = (
    ev: RadioChangeEvent,
    formik: FormikProps<AssignmentRequirementsFormValues>,
  ) => {
    if (!ev.target.value) {
      formik.setFieldValue('specialInstructions', '', false);
      formik.setFieldValue('onboardingDocumentation', [], false);

      setOnboardingDocumentationFileInfo([]);
    }
  };

  return (
    <Formik
      enableReinitialize
      innerRef={formRef}
      initialValues={predefinedInitialValues || initialValues}
      validationSchema={validationSchema}
      onSubmit={() => {}}
    >
      {(formik) => (
        <>
          <FormikEffect onChange={onChange} />

          <Form layout="vertical">
            <div className="modal-title">Assignment requirements</div>
            <Row gutter={40}>
              <Col xs={24} md={12}>
                <FormItem
                  name="contractorsPermitted"
                  label="Independent contractors permitted"
                  data-id="editCustomerContractorsPermittedField"
                  className="inline-radio"
                >
                  <Radio.Group
                    className="radio"
                    name="contractorsPermitted"
                    options={[
                      {
                        label: 'Yes',
                        value: true,
                      },
                      {
                        label: 'No',
                        value: false,
                      },
                    ]}
                  />
                </FormItem>
              </Col>

              <Col xs={24} md={12}>
                <FormItem
                  name="designatedPreScreening"
                  label="Designated pre-screening provider"
                  data-id="editCustomerDesignatedPreScreeningField"
                  className="inline-radio"
                >
                  <Radio.Group
                    className="radio"
                    name="designatedPreScreening"
                    options={[
                      {
                        label: 'Yes',
                        value: true,
                      },
                      {
                        label: 'No',
                        value: false,
                      },
                    ]}
                  />
                </FormItem>
              </Col>

              <Col xs={24} md={12}>
                <FormItem
                  name="preScreeningRequired"
                  label="Pre-screening required"
                  data-id="editCustomerPreScreeningRequiredField"
                  className="inline-radio"
                >
                  <Radio.Group
                    className="radio"
                    name="preScreeningRequired"
                    onChange={(ev) => handlePreScreeningChange(ev, formik)}
                    options={[
                      {
                        label: 'Yes',
                        value: true,
                      },
                      {
                        label: 'No',
                        value: false,
                      },
                    ]}
                  />
                </FormItem>
              </Col>

              <Col xs={24} md={12}>
                <FormItem
                  name="preScreeningBilledTo"
                  label="Pre-screening billed to"
                  data-id="editCustomerPreScreeningBilledToField"
                  className="inline-radio"
                >
                  <Radio.Group
                    className="radio"
                    name="preScreeningBilledTo"
                    disabled={!formik.values.preScreeningRequired}
                    options={[
                      {
                        label: preScreeningBilledMemberLabel,
                        value: CustomerPreScreeningProviderEnum.Member,
                      },
                      {
                        label: preScreeningBilledCustomerLabel,
                        value: CustomerPreScreeningProviderEnum.Customer,
                      },
                      {
                        label: preScreeningBilledMBPLabel,
                        value: CustomerPreScreeningProviderEnum.MBP,
                      },
                    ]}
                  />
                </FormItem>
              </Col>

              <Col xs={24} md={12}>
                <FormItem
                  name="preScreening"
                  label="Pre-screening"
                  data-id="editCustomerPreScreeningField"
                >
                  <Input
                    name="preScreening"
                    placeholder="Enter pre-screening"
                    autoComplete="off"
                  />
                </FormItem>
              </Col>

              <Col xs={24} md={12}>
                <FormItem
                  name="isOnboardingDocumentationRequired"
                  label="Onboarding documentation required"
                  data-id="editCustomerOnboardingDocumentationField"
                  className="inline-radio"
                >
                  <Radio.Group
                    className="radio"
                    name="isOnboardingDocumentationRequired"
                    onChange={(ev) =>
                      handleOnboardingDocumentationChange(ev, formik)
                    }
                    options={[
                      {
                        label: 'Yes',
                        value: true,
                      },
                      {
                        label: 'No',
                        value: false,
                      },
                    ]}
                  />
                </FormItem>
              </Col>
            </Row>

            <Row gutter={40}>
              <Col xs={24} md={12}>
                <FormItem
                  name="specialInstructions"
                  label="Special instructions"
                  data-id="editCustomerSpecialInstructionsField"
                >
                  <Input
                    name="specialInstructions"
                    disabled={!formik.values.isOnboardingDocumentationRequired}
                    placeholder="Enter special instructions"
                    autoComplete="off"
                  />
                </FormItem>
              </Col>

              {formik.values.isOnboardingDocumentationRequired && (
                <Col xs={24} md={12}>
                  <div className="upload-wrapper">
                    <FormItem
                      name="onboardingDocumentation"
                      label="Onboarding documentation"
                      data-id="editCustomerOnboardingDocumentationField"
                      showValidateError={false}
                    >
                      <OnboardingDocumentationUploader
                        fieldName="onboardingDocumentation"
                        files={onboardingDocumentationFileInfo}
                      />
                    </FormItem>
                  </div>
                </Col>
              )}

              <Col xs={24} md={12}>
                <FormItem
                  name="terminationInstructions"
                  label="Termination instructions"
                  data-id="editCustomerTerminationInstructionsField"
                >
                  <Input
                    name="terminationInstructions"
                    placeholder="Enter termination instructions"
                    autoComplete="off"
                  />
                </FormItem>
              </Col>
            </Row>

            <div className="modal-title">Special assignment requirements</div>

            <Row gutter={40}>
              <Col xs={24} md={12}>
                <FormItem
                  name="workPerformance"
                  label="Guaranteed work performance"
                  data-id="editCustomerWorkPerformanceField"
                >
                  <Input
                    name="workPerformance"
                    placeholder="Enter guaranteed work performance"
                    autoComplete="off"
                  />
                </FormItem>
              </Col>

              <Col xs={24} md={12}>
                <FormItem
                  name="tempToPermConversion"
                  label="Temp-to-perm conversion"
                  data-id="editCustomerTempToPermConversionField"
                >
                  <Input
                    name="tempToPermConversion"
                    placeholder="Enter temp-to-perm conversion"
                    autoComplete="off"
                  />
                </FormItem>
              </Col>

              <Col xs={24} md={12}>
                <FormItem
                  name="timeEntry"
                  label="Time entry method"
                  data-id="editCustomerTimeEntryField"
                >
                  <Input
                    name="timeEntry"
                    placeholder="Enter time entry method"
                    autoComplete="off"
                  />
                </FormItem>
              </Col>

              <Col xs={24} md={12}>
                <FormItem
                  name="assignmentNotes"
                  label="Added notes"
                  data-id="editCustomerAssignmentNotesField"
                >
                  <Input
                    name="assignmentNotes"
                    placeholder="Enter added notes"
                    autoComplete="off"
                  />
                </FormItem>
              </Col>
            </Row>
          </Form>
        </>
      )}
    </Formik>
  );
}
